import {
  CONFIG_ELEMENTS,
  TestAnswerFeedbackDisplayOption,
  type FeedbackRTValue,
} from '@vedalib/editor/lib/elements'
import { useDebounceFn, useUpdateEffect } from 'ahooks'
import * as R from 'ramda'

import { buildTestAnnouncement, useLiveFeedback } from 'components/Editor/EditorElements/courseKit/CourseKitUtils'
import ScreenReaderOnlyText from 'components/Editor/styled/ScreenReaderOnlyText'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import { useProjectT } from 'services/ProjectLabels'
import { setQuestion } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getBlockState, getIsInteractiveMode } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { testPropsEl } from 'utils/test/qaData'

import TestFeedback from '../../courseKit/TestFeedback'
import { useElementRootStyles } from '../../hooks/useElementRootStyles'
import { getFeedbackValue } from '../../utils/getFeedbackValue'
import InputStyled from './styled/InputStyled'
import type { TestKeyboardInputElementType } from './testKeyboardInputTypes'

const TestKeyboardInputElement: TestKeyboardInputElementType = ({
  element: { id: elementId, value },
  styles,
  feedback,
  font,
  state,
  block,
  mode,
  onChange,
}) => {
  const pt = useProjectT()
  const dispatch = useProjectDispatch()
  const blockResult = useProjectContext(getBlockState, block?.uuid)?.result
  const isPreview = useProjectContext(getIsInteractiveMode)
  const inputValue = state?.rawValue || ''
  const feedbackValue = getFeedbackValue(value)
  const items = value?.items || []
  const { generalFbValid } = state || {}
  const id = `${elementId}-testKeyboardInput`

  const disabled = Boolean(blockResult)
  const isTrueAnswer = Boolean(state?.value?.length)

  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId,
    mode,
  })
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.testKeyboardInput.type,
      elementId,
    },
  )

  const onSendAnalytics = useDebounceFn(
    (inputValue: string) => {
      const data = entityAnalyticsConfig.interacted({
        resultExtensions: { label: inputValue, elementId },
        objectExtensions: baseObjectExtensions,
        elementId,
      })
      sendXapiData(data)
    },
    { wait: 1500 },
  ).run

  const onInternalInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const elGuessed = items.find((el) => el.label.toLowerCase() === inputValue.toLowerCase())
    const value = elGuessed ? items.map((item) => item.value) : []
    onSendAnalytics(inputValue)
    dispatch(
      setQuestion({
        elementId,
        value: { value, rawValue: inputValue },
        isReady: Boolean(inputValue),
        blockId: block?.uuid || '',
      }),
    )
  }

  const handleChangeGeneralFeedback = (newFeedbackValue: FeedbackRTValue) => {
    onChange?.(R.assoc('generalFeedback', newFeedbackValue, value))
  }

  const rootStyles = useElementRootStyles(styles.root)
  const announce = useLiveFeedback('polite')

  useUpdateEffect(() => {
    if (!blockResult) {
      return
    }
    const correctAnswer = items.map((item) => item.label).join(', ')

    announce(
      buildTestAnnouncement(
        blockResult.isValid,
        correctAnswer || undefined,
        inputValue || undefined,
      ),
    )
  }, [blockResult])

  return (
    <div style={rootStyles}>
      <TestFeedback
        blockUuid={block?.uuid}
        feedbackValue={feedbackValue}
        font={font || {}}
        isTrueAnswer={isTrueAnswer}
        label={inputValue as string}
        mode={mode}
        showAnswerFb={value.answerFeedback}
        styles={feedback}
      >
        <ScreenReaderOnlyText as='label' htmlFor={id}>
          Keyboard input
        </ScreenReaderOnlyText>
        <InputStyled
          $fonts={font}
          $styles={styles}
          {...testPropsEl('courseInput', {})}
          autoComplete='off'
          disabled={disabled}
          id={id}
          name='testKeyboardInput'
          onChange={onInternalInputChange}
          placeholder={pt('elements.testKeyboardInput.placeholder')}
          value={inputValue as string}
        />
      </TestFeedback>
      <TestFeedback
        blockUuid={block?.uuid}
        feedbackValue={feedbackValue}
        font={font || {}}
        isTrueAnswer={isPreview ? isTrueAnswer : generalFbValid}
        mode={mode}
        onChange={handleChangeGeneralFeedback}
        rtProps={{
          activeIndex,
          isActiveElement,
          onLabelSelect,
          isFill,
          value: value.generalFeedback,
        }}
        showFeedback={
          value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.perOption
        }
        styles={feedback}
      />
    </div>
  )
}

export default TestKeyboardInputElement
